/**
 * Minimal date formatting/parsing covering the moment.js token subset this app
 * actually uses. Replaces moment (~60 kB in the entry chunk) with zero deps.
 *
 * Token semantics deliberately match moment so existing call sites render
 * identically — note `hh`/`h` are 12-hour and `a`/`A` are lower/uppercase.
 * See datetime.spec.ts for the parity tests against moment's own output.
 */

const pad = (n: number, width = 2) => String(n).padStart(width, '0')

/** Matching moment's English abbreviations exactly — note "Sep", not "Sept". */
const SHORT_MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
]

const LONG_MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
]

/** Tokens longest-first so `YYYY` wins over `YY`, `mm` over `m`, etc. */
const FORMAT_TOKENS: Record<string, (d: Date) => string> = {
    YYYY: (d) => String(d.getFullYear()),
    YY: (d) => pad(d.getFullYear() % 100),
    /*
     * Named months, added for the belt history dates — "22 Feb 2026" places a
     * grading better than "22/02/2026".
     *
     * Longest-first ordering matters here: without MMMM and MMM in the table,
     * 'D MMM YYYY' matched MM then M and rendered "22 022 2026".
     */
    MMMM: (d) => LONG_MONTHS[d.getMonth()],
    MMM: (d) => SHORT_MONTHS[d.getMonth()],
    MM: (d) => pad(d.getMonth() + 1),
    M: (d) => String(d.getMonth() + 1),
    DD: (d) => pad(d.getDate()),
    D: (d) => String(d.getDate()),
    HH: (d) => pad(d.getHours()),
    H: (d) => String(d.getHours()),
    hh: (d) => pad(((d.getHours() + 11) % 12) + 1),
    h: (d) => String(((d.getHours() + 11) % 12) + 1),
    mm: (d) => pad(d.getMinutes()),
    m: (d) => String(d.getMinutes()),
    ss: (d) => pad(d.getSeconds()),
    s: (d) => String(d.getSeconds()),
    A: (d) => (d.getHours() < 12 ? 'AM' : 'PM'),
    a: (d) => (d.getHours() < 12 ? 'am' : 'pm')
}

const TOKEN_RE = new RegExp(
    Object.keys(FORMAT_TOKENS)
        .sort((a, b) => b.length - a.length)
        .join('|'),
    'g'
)

/** Formats a Date using moment-style tokens (e.g. 'DD/MM/YYYY hh:mm'). */
export function formatPattern(date: Date, pattern: string): string {
    return pattern.replace(TOKEN_RE, (token) => FORMAT_TOKENS[token](date))
}

/** Capture-group builders for the tokens we support when parsing. */
const PARSE_TOKENS: Record<string, string> = {
    YYYY: '(\\d{4})',
    MM: '(\\d{1,2})',
    M: '(\\d{1,2})',
    DD: '(\\d{1,2})',
    D: '(\\d{1,2})',
    HH: '(\\d{1,2})',
    H: '(\\d{1,2})',
    hh: '(\\d{1,2})',
    h: '(\\d{1,2})',
    mm: '(\\d{1,2})',
    m: '(\\d{1,2})',
    ss: '(\\d{1,2})',
    s: '(\\d{1,2})',
    A: '([AaPp][Mm])',
    a: '([AaPp][Mm])'
}

const PARSE_TOKEN_RE = new RegExp(
    Object.keys(PARSE_TOKENS)
        .sort((a, b) => b.length - a.length)
        .join('|'),
    'g'
)

/**
 * Parses `value` against a moment-style `pattern`, returning a Date on today's
 * calendar date (matching moment's behaviour for time-only patterns).
 * Returns null when the value doesn't match.
 */
export function parsePattern(value: string, pattern: string, base = new Date()): Date | null {
    const order: string[] = []
    let source = ''
    let last = 0

    // Walk the pattern, converting tokens to capture groups and escaping literals.
    pattern.replace(PARSE_TOKEN_RE, (token, offset: number) => {
        source += pattern.slice(last, offset).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        source += PARSE_TOKENS[token]
        order.push(token)
        last = offset + token.length
        return token
    })
    source += pattern.slice(last).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

    const match = new RegExp(`^\\s*${source}\\s*$`).exec(value)
    if (!match) return null

    let year = base.getFullYear()
    let month = base.getMonth()
    let day = base.getDate()
    let hours = 0
    let minutes = 0
    let seconds = 0
    let meridiem: 'am' | 'pm' | null = null
    let hoursAre12h = false

    order.forEach((token, i) => {
        const raw = match[i + 1]
        switch (token) {
            case 'YYYY':
                year = Number(raw)
                break
            case 'YY':
                year = 2000 + Number(raw)
                break
            case 'MM':
            case 'M':
                month = Number(raw) - 1
                break
            case 'DD':
            case 'D':
                day = Number(raw)
                break
            case 'HH':
            case 'H':
                hours = Number(raw)
                break
            case 'hh':
            case 'h':
                hours = Number(raw)
                hoursAre12h = true
                break
            case 'mm':
            case 'm':
                minutes = Number(raw)
                break
            case 'ss':
            case 's':
                seconds = Number(raw)
                break
            case 'A':
            case 'a':
                meridiem = raw.toLowerCase() as 'am' | 'pm'
                break
        }
    })

    if (hoursAre12h && meridiem) {
        hours = hours % 12
        if (meridiem === 'pm') hours += 12
    }

    return new Date(year, month, day, hours, minutes, seconds)
}

/** Minutes elapsed since midnight — handy for comparing times on one day. */
export function minutesOfDay(date: Date): number {
    return date.getHours() * 60 + date.getMinutes()
}

/**
 * Time patterns seen in API payloads, most specific first. The gym timetable
 * returns 24-hour "16:00:00" while some inputs use "4:00 PM", so callers that
 * receive either should use `parseTime` rather than guessing a single pattern.
 *
 * (moment parsed leniently and accepted a mismatched format; parsePattern is
 * strict, so the tolerated formats are listed explicitly here instead.)
 */
const TIME_PATTERNS = ['HH:mm:ss', 'h:mm A', 'HH:mm', 'h:mm']

/** Parses a time string against any supported pattern. Returns null if none match. */
export function parseTime(value: string, base = new Date()): Date | null {
    for (const pattern of TIME_PATTERNS) {
        const parsed = parsePattern(value, pattern, base)
        if (parsed) return parsed
    }
    return null
}
